/**
 * Performance optimization utilities
 */

// Preload critical resources
export const preloadCriticalResources = () => {
  const criticalImages = [
    '/lovable-uploads/79354011-79de-4b95-8794-c108a7f7938b.webp',
    '/lovable-uploads/84e2b7fc-7fdf-49ef-8784-b4f98f0c4f22.webp'
  ];

  criticalImages.forEach(src => {
    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'image';
    link.href = src;
    link.type = 'image/webp';
    document.head.appendChild(link);
  });
};

// Prefetch next page resources
export const prefetchRoute = (path: string) => {
  const link = document.createElement('link');
  link.rel = 'prefetch';
  link.href = path;
  document.head.appendChild(link);
};

// Measure and report performance metrics
export const reportWebVitals = (metric: any) => {
  if (process.env.NODE_ENV === 'production') {
    // Report to analytics service
    console.log('Web Vital:', metric);
  }
};

// Optimize animations for performance
export const getOptimizedMotionProps = () => ({
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { 
    duration: 0.3, 
    ease: [0.4, 0, 0.2, 1],
    willChange: 'transform, opacity'
  }
});

// Service Worker registration - disabled to prevent caching issues
// Users will always see the latest version of the site
export const registerSW = async () => {
  // Unregister any existing service workers to clear stale caches
  if ('serviceWorker' in navigator) {
    try {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map(r => r.unregister()));
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(cacheNames.map(name => caches.delete(name)));
      }
    } catch (error) {
      // Silently fail - not critical
    }
  }
};

// Intersection Observer for lazy loading
export const createIntersectionObserver = (
  callback: IntersectionObserverCallback,
  options: IntersectionObserverInit = {}
) => {
  const defaultOptions = {
    root: null,
    rootMargin: '50px',
    threshold: 0.1,
    ...options
  };
  
  return new IntersectionObserver(callback, defaultOptions);
};

// DOM size optimization
export const optimizeDOMSize = () => {
  // Remove unused elements
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.removedNodes.forEach((node) => {
        if (node.nodeType === Node.ELEMENT_NODE) {
          // Clean up event listeners
          const element = node as Element;
          element.removeEventListener?.('click', () => {});
        }
      });
    });
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true
  });

  return observer;
};

export default {
  preloadCriticalResources,
  prefetchRoute,
  reportWebVitals,
  getOptimizedMotionProps,
  registerSW,
  createIntersectionObserver,
  optimizeDOMSize
};